import { useEffect, useState } from "react";
import { Copy, Check, Share2, MousePointerClick, Users, Clock, CheckCircle2, Globe, Wallet, IndianRupee, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtINR, fmtDateTime } from "../../lib/api";
import { StatCard, PageHeader, StatusBadge, TableShell, TR, TD, LoadingRows, EmptyState } from "../../components/Shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

const PAYOUT_FIELDS = [
  { k: "account_name", l: "Account Holder Name" },
  { k: "bank_name", l: "Bank Name" },
  { k: "account_number", l: "Account Number" },
  { k: "ifsc", l: "IFSC Code" },
  { k: "upi_id", l: "UPI ID (optional)" },
];

export default function PartnerReferrals() {
  const [data, setData] = useState(null);
  const [customers, setCustomers] = useState(null);
  const [payout, setPayout] = useState(null);
  const [crm, setCrm] = useState(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/partner/referral").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/partner/referral/customers").then((r) => setCustomers(r.data.customers)).catch(() => setCustomers([]));
    api.get("/partner/payout-details").then((r) => setPayout(r.data.payout_details)).catch(() => setPayout({}));
    api.get("/partner/crm-earnings").then((r) => setCrm(r.data)).catch(() => setCrm(null));
  };
  useEffect(() => { load(); }, []);

  if (!data) return <LoadingRows rows={5} />;
  const link = `${window.location.origin}/start/${data.code}`;
  const s = data.stats;

  const copy = async () => {
    try { await navigator.clipboard.writeText(link); }
    catch {
      const ta = document.createElement("textarea");
      ta.value = link; document.body.appendChild(ta); ta.select();
      document.execCommand("copy"); document.body.removeChild(ta);
    }
    setCopied(true);
    toast.success("Referral link copied");
    setTimeout(() => setCopied(false), 2000);
  };

  const savePayout = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/partner/payout-details", payout);
      toast.success("Payout details saved");
      load();
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="partner-referrals-page">
      <PageHeader title="Referrals & Earnings" subtitle="Share your unique link — earn commission on every website created through it." testid="partner-referrals-title" />

      <div data-testid="referral-link-card" className="bg-card border border-border rounded-2xl p-6 mb-8">
        <p className="overline-label mb-3">Your Referral Link</p>
        <div className="flex flex-wrap items-center gap-3">
          <code data-testid="referral-link-text" className="flex-1 min-w-52 truncate rounded-lg bg-muted px-4 py-3 text-sm font-mono font-semibold">{link}</code>
          <Button data-testid="copy-referral-link" onClick={copy} className="rounded-full font-bold">
            {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />} {copied ? "Copied" : "Copy Link"}
          </Button>
          <a data-testid="share-whatsapp" target="_blank" rel="noopener noreferrer"
            href={`https://wa.me/?text=${encodeURIComponent(`Create your business website online — themes, store, orders, everything included. Start here: ${link}`)}`}
            className="inline-flex items-center gap-2 rounded-full border border-emerald-500/40 text-emerald-600 dark:text-emerald-400 px-5 h-10 text-sm font-bold hover:bg-emerald-500/10 transition-colors">
            <MessageCircle className="h-4 w-4" /> WhatsApp
          </a>
        </div>
        <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1.5"><Share2 className="h-3.5 w-3.5" /> Anyone who signs up through this link is automatically attributed to you — even if they come back later.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-10">
        <StatCard icon={MousePointerClick} label="Link Clicks" value={s.clicks} testid="ref-stat-clicks" />
        <StatCard icon={Users} label="Signups" value={s.signups} testid="ref-stat-signups" delay={0.05} />
        <StatCard icon={Clock} label="Payments Pending" value={s.payments_pending} testid="ref-stat-pending" delay={0.1} />
        <StatCard icon={CheckCircle2} label="Approved" value={s.approved} testid="ref-stat-approved" delay={0.15} />
        <StatCard icon={Globe} label="Websites Live" value={s.websites_created} testid="ref-stat-websites" delay={0.2} />
        <StatCard icon={IndianRupee} label="Commission Pending" value={fmtINR(s.commission_pending)} sub="Awaiting payout" testid="ref-stat-comm-pending" delay={0.25} />
        <StatCard icon={Wallet} label="Total Earned" value={fmtINR(s.commission_total)} testid="ref-stat-comm-total" delay={0.3} />
        <StatCard icon={CheckCircle2} label="Paid Out" value={fmtINR(s.commission_paid)} testid="ref-stat-comm-paid" delay={0.35} />
      </div>

      {crm && (
        <div className="mb-10">
          <h2 className="font-display font-bold text-2xl tracking-tight mb-4">CRM Earnings</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6">
            <StatCard icon={Users} label="CRM Customers" value={crm.customers} sub={`${crm.active_subscribers} active`} testid="crm-earn-customers" />
            <StatCard icon={IndianRupee} label="CRM Revenue Generated" value={fmtINR(crm.revenue_generated)} sub={`${crm.payments} payments · ${crm.renewals} renewals`} testid="crm-earn-revenue" delay={0.05} />
            <StatCard icon={Wallet} label="CRM Commission" value={fmtINR(crm.commission_total)} sub={`Pending ${fmtINR(crm.commission_pending)}`} testid="crm-earn-commission" delay={0.1} />
            <StatCard icon={CheckCircle2} label="CRM Commission Paid" value={fmtINR(crm.commission_paid)} testid="crm-earn-paid" delay={0.15} />
          </div>
          {crm.subscribers.length > 0 && (
            <TableShell head={["Business", "Status", "Next Renewal", "Renewals", "Total Paid", "Your Commission"]} testid="crm-earnings-table">
              {crm.subscribers.map((s, i) => (
                <TR key={i} testid={`crm-earn-sub-${i}`}>
                  <TD className="font-semibold">{s.business}</TD>
                  <TD><StatusBadge status={s.status} /></TD>
                  <TD className="text-sm text-muted-foreground">{s.expires_at ? fmtDateTime(s.expires_at).split(",")[0] : "—"}</TD>
                  <TD>{s.renewals}</TD>
                  <TD>{fmtINR(s.total_paid)}</TD>
                  <TD className="font-semibold">{fmtINR(s.commission)}</TD>
                </TR>
              ))}
            </TableShell>
          )}
        </div>
      )}

      <h2 className="font-display font-bold text-2xl tracking-tight mb-4">Referred Customers</h2>
      {customers === null ? (
        <div className="bg-card border border-border rounded-2xl p-8 mb-10"><LoadingRows rows={3} /></div>
      ) : customers.length === 0 ? (
        <div className="mb-10">
          <EmptyState icon={Users} title="No referrals yet" body="Share your link on WhatsApp, Instagram or with your clients — signups will appear here." />
        </div>
      ) : (
        <div className="mb-10">
          <TableShell head={["Customer", "Business", "Request Status", "Payment", "Amount", "Date"]} testid="referral-customers-table">
            {customers.map((c) => (
              <TR key={c.request_id} testid={`ref-customer-${c.request_id}`}>
                <TD className="font-semibold">{c.name || "—"}</TD>
                <TD>{c.business || "—"}</TD>
                <TD><StatusBadge status={c.status} /></TD>
                <TD>{c.payment_status ? <StatusBadge status={c.payment_status} /> : "—"}</TD>
                <TD className="font-semibold">{c.amount ? fmtINR(c.amount) : "—"}</TD>
                <TD className="text-muted-foreground text-sm">{fmtDateTime(c.created_at)}</TD>
              </TR>
            ))}
          </TableShell>
        </div>
      )}

      <h2 className="font-display font-bold text-2xl tracking-tight mb-4">Payout Details</h2>
      <form data-testid="payout-details-form" onSubmit={savePayout} className="bg-card border border-border rounded-2xl p-6 grid sm:grid-cols-2 gap-4 max-w-3xl">
        {PAYOUT_FIELDS.map((f) => (
          <div key={f.k} className="space-y-1.5">
            <Label className="overline-label">{f.l}</Label>
            <Input data-testid={`payout-${f.k}`} value={payout?.[f.k] || ""} onChange={(e) => setPayout({ ...payout, [f.k]: e.target.value })}
              className="h-12 rounded-md bg-background" placeholder={payout?.[f.k]?.includes("•") ? `Current: ${payout[f.k]}` : ""} />
          </div>
        ))}
        <div className="sm:col-span-2 flex items-center gap-4">
          <Button data-testid="payout-save" type="submit" disabled={busy} className="rounded-full font-bold">{busy ? "Saving..." : "Save Payout Details"}</Button>
          <p className="text-xs text-muted-foreground">Used by the platform team to transfer your commission payouts.</p>
        </div>
      </form>
    </div>
  );
}
